
import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { CRMUser, CRMUserFormData } from '@/types/crm';
import { useUserState } from './useUserState';
import { useEmailTracking } from './useEmailTracking';

// Mock data store - in a real app, this would be replaced with API calls
let crmUsers: CRMUser[] = [];

export const useCrmUsers = () => {
  const { toast } = useToast();
  const {
    users,
    selectedUser,
    isLoading,
    searchQuery,
    processingUsers,
    setUsers,
    setSelectedUser,
    setIsLoading,
    setSearchQuery,
    setProcessingUsers
  } = useUserState();

  const { markEmailAsDelivered, markEmailAsOpened } = useEmailTracking(
    users,
    setUsers,
    selectedUser,
    setSelectedUser
  );

  // Load users
  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      // In a real app, this would be an API call
      await new Promise(resolve => setTimeout(resolve, 500));
      setUsers([...crmUsers]);
    } catch (error) {
      console.error('Error loading CRM users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load CRM users',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, setUsers, setIsLoading]);

  // Add a new user
  const addUser = useCallback(async (userData: CRMUserFormData) => {
    try {
      // Check for duplicate email
      const isDuplicate = crmUsers.some(user => 
        user.email.toLowerCase() === userData.email.toLowerCase()
      );

      if (isDuplicate) {
        throw new Error('A user with this email already exists');
      }

      const newUser: CRMUser = {
        id: `user-${Date.now()}`,
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        status: 'new',
        dateAdded: new Date().toISOString(),
        totalReferrals: 0,
        referralHistory: [],
        notes: userData.notes || '',
        tags: userData.tags || [],
      };

      crmUsers.push(newUser);
      setUsers([...crmUsers]);

      toast({
        title: 'Success',
        description: 'User added to CRM successfully',
      });

      return newUser;
    } catch (error) {
      console.error('Error adding CRM user:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add user to CRM',
        variant: 'destructive',
      });
      throw error;
    }
  }, [toast, setUsers]);

  // Send a referral email
  const sendReferralEmail = useCallback(async (userId: string, templateId: string, templateName: string) => {
    // First create a copy of the current processingUsers
    const updatedProcessingUsers = [...processingUsers, userId];
    setProcessingUsers(updatedProcessingUsers);
    
    try {
      // In a real app, this would send an actual email
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const emailId = `email-${Date.now()}`;
      const sentDate = new Date().toISOString();
      
      // Create email history entry
      const emailHistory = {
        id: emailId,
        templateId,
        templateName,
        sentDate,
        status: 'sent' as const
      };
      
      // Update user in the "database"
      crmUsers = crmUsers.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            status: 'contacted' as const,
            lastContactDate: sentDate,
            referralHistory: [...user.referralHistory, emailHistory]
          };
        }
        return user;
      });
      
      // Update state
      setUsers([...crmUsers]);
      
      // If user is selected, update selected user
      if (selectedUser && selectedUser.id === userId) {
        const updatedUser = crmUsers.find(u => u.id === userId);
        if (updatedUser) {
          setSelectedUser(updatedUser);
        }
      }
      
      toast({
        title: 'Email Sent',
        description: `Referral invitation sent to ${crmUsers.find(u => u.id === userId)?.name}`,
      });
      
      // Simulate delivery after 2 seconds
      setTimeout(() => {
        markEmailAsDelivered(userId, emailId);
      }, 2000);
      
      // Simulate opening after 5 seconds (in a real app this would use tracking pixels or links)
      setTimeout(() => {
        markEmailAsOpened(userId, emailId);
      }, 5000);
      
      return true;
    } catch (error) {
      console.error('Error sending referral email:', error);
      toast({
        title: 'Error',
        description: 'Failed to send referral email',
        variant: 'destructive',
      });
      return false;
    } finally {
      // Remove the userId from processingUsers
      const filteredProcessingUsers = processingUsers.filter(id => id !== userId);
      setProcessingUsers(filteredProcessingUsers);
    }
  }, [toast, selectedUser, setUsers, setSelectedUser, processingUsers, setProcessingUsers, markEmailAsDelivered, markEmailAsOpened]);

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      (user.phone && user.phone.includes(query))
    );
  });

  return {
    users,
    filteredUsers,
    selectedUser,
    setSelectedUser,
    isLoading,
    searchQuery,
    setSearchQuery,
    processingUsers,
    loadUsers,
    addUser,
    sendReferralEmail
  };
};
